import React, { Fragment, useState } from 'react';
import { Link, useLocation } from 'react-router';
import {
    BookOutlined,
    BorderOuterOutlined,
    ColumnHeightOutlined,
    ColumnWidthOutlined,
    CommentOutlined,
    LogoutOutlined,
    MailOutlined,
    QuestionCircleOutlined,
    ReadOutlined,
    SettingOutlined,
    TeamOutlined,
    UserOutlined
} from '@ant-design/icons';
import { Avatar, Button, Col, Dropdown, Layout, Menu, Row, Spin, Tooltip } from 'antd';
import { checkMainMenuAccess } from '../../config';
import { useDensity } from '../../context/DensityContext';
import { useCurrentUser } from '../../context/UserContext';
import { useLogout } from '../../hooks/useAuth';
import { CopyToClipboard } from '../CopyToClipboard';
import { Glossary } from './Glossary';
import { HelpCenter } from './HelpCenter';
import { ThemeToggle } from './ThemeToggle';

export const HeaderLayout = () => {
    const { user, accesses } = useCurrentUser();
    const { pathname } = useLocation();
    const logout = useLogout();
    const { density, toggle: toggleDensity } = useDensity();
    const [glossaryOpen, setGlossaryOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);

    const { firstName = 'Klickly', lastName = 'Admin', photo = null, ip } = user || {};

    const onLogoutClick = () => logout.mutate();

    const internalDocsUrl = `${process.env.REACT_APP_ADMIN_API_URL || ''}/internal-docs/`;

    const items = [
        {
            key: 'ip',
            label: (
                <CopyToClipboard
                    value={ip}
                    successMessage='IP address successfully copied to clipboard'
                    content={
                        <span>
                            <BorderOuterOutlined /> IP: {ip}
                        </span>
                    }
                />
            )
        },
        {
            key: 'logout',
            label: (
                <span
                    role='button'
                    tabIndex={0}
                    onClick={onLogoutClick}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onLogoutClick();
                        }
                    }}
                >
                    <LogoutOutlined /> Logout
                </span>
            )
        }
    ];

    const menuItems = [
        {
            key: 'brands-v2',
            label: (
                <Fragment>
                    <Link to='/brands-v2'>
                        <TeamOutlined />
                        <span>Brands</span>
                    </Link>
                </Fragment>
            )
        },
        {
            key: 'kcp',
            label: (
                <Fragment>
                    <Link to='/kcp' title='KCP — Klickly Consumer Platform'>
                        <CommentOutlined />
                        <span>KCP</span>
                    </Link>
                </Fragment>
            )
        },
        {
            key: 'notifications',
            label: (
                <Fragment>
                    <Link to='/notifications'>
                        <MailOutlined />
                        <span>Notifications admin</span>
                    </Link>
                </Fragment>
            )
        },
        {
            key: 'system',
            label: (
                <Fragment>
                    <Link to='/system'>
                        <SettingOutlined />
                        <span>System admin</span>
                    </Link>
                </Fragment>
            )
        }
    ];

    const selectedKey = pathname.split('/')[1] || 'brands-v2';

    return (
        <Layout.Header className='header' role='banner'>
            <Row>
                <Col span={18}>
                    <Menu
                        role='navigation'
                        aria-label='Primary'
                        selectedKeys={[selectedKey]}
                        mode='horizontal'
                        items={menuItems.filter((item) => checkMainMenuAccess(item.key, accesses))}
                    />
                </Col>
                <Col span={6}>
                    <div className='right'>
                        <Tooltip title='Internal docs — open in a new tab'>
                            <Button
                                type='text'
                                size='small'
                                href={internalDocsUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                icon={<ReadOutlined />}
                                aria-label='Open internal docs'
                                style={{ marginRight: 4 }}
                            />
                        </Tooltip>
                        <Tooltip title='How do I…? — jump to the right page by intent'>
                            <Button
                                type='text'
                                size='small'
                                icon={<QuestionCircleOutlined />}
                                onClick={() => setHelpOpen(true)}
                                aria-label='Open help center'
                                style={{ marginRight: 4 }}
                            />
                        </Tooltip>
                        <Tooltip title='Glossary — internal acronyms used across the admin'>
                            <Button
                                type='text'
                                size='small'
                                icon={<BookOutlined />}
                                onClick={() => setGlossaryOpen(true)}
                                aria-label='Open glossary'
                                style={{ marginRight: 4 }}
                            />
                        </Tooltip>
                        <Tooltip
                            title={
                                density === 'compact' ? 'Switch to comfortable density' : 'Switch to compact density'
                            }
                        >
                            <Button
                                type='text'
                                size='small'
                                icon={density === 'compact' ? <ColumnHeightOutlined /> : <ColumnWidthOutlined />}
                                onClick={toggleDensity}
                                aria-label={`Density: ${density}. Click to toggle.`}
                                style={{ marginRight: 4 }}
                            />
                        </Tooltip>
                        <ThemeToggle />
                        {firstName ? (
                            <Dropdown menu={{ items }} aria-label='Account menu'>
                                <span
                                    className='action account'
                                    role='button'
                                    tabIndex={0}
                                    aria-label={`Account menu for ${firstName} ${lastName}`}
                                >
                                    <Avatar size='small' className='avatar' icon={<UserOutlined />} src={photo} />
                                    <span className='name'>
                                        {firstName} {lastName}
                                    </span>
                                </span>
                            </Dropdown>
                        ) : (
                            <Spin size='small' style={{ marginLeft: 8 }} aria-label='Loading user' />
                        )}
                    </div>
                </Col>
            </Row>
            <Glossary open={glossaryOpen} onClose={() => setGlossaryOpen(false)} />
            <HelpCenter open={helpOpen} onClose={() => setHelpOpen(false)} />
        </Layout.Header>
    );
};
